import type { BiomeType } from '../../model/gamestate/GameState';
import { getTerrainTexturePath, type TerrainTextureKey } from '../../terrain/registry';

interface TextureRecord {
  image: HTMLImageElement;
  loaded: boolean;
  failed: boolean;
}

const textureRecords = new Map<TerrainTextureKey, TextureRecord>();
type TerrainTextureUpdateListener = (key: TerrainTextureKey) => void;

const listeners = new Set<TerrainTextureUpdateListener>();

function notifyListeners(key: TerrainTextureKey): void {
  for (const listener of listeners) {
    listener(key);
  }
}

function ensureTextureRecord(key: TerrainTextureKey): TextureRecord | null {
  const path = getTerrainTexturePath(key);
  if (!path) {
    return null;
  }
  const existing = textureRecords.get(key);
  if (existing) {
    return existing;
  }

  const image = new Image();
  const record: TextureRecord = {
    image,
    loaded: false,
    failed: false,
  };

  image.onload = () => {
    record.loaded = true;
    notifyListeners(key);
  };
  image.onerror = () => {
    record.failed = true;
  };
  image.src = path;

  textureRecords.set(key, record);
  return record;
}

export function getTerrainTexture(key: TerrainTextureKey): HTMLImageElement | null {
  const record = ensureTextureRecord(key);
  if (!record || !record.loaded || record.failed) {
    return null;
  }
  return record.image;
}

export function getTileTextureKey(tile: {
  terrain: 'floor' | 'wall' | 'portal' | 'path';
  biome?: BiomeType;
  walkable: boolean;
}): TerrainTextureKey | null {
  if (tile.terrain === 'portal') {
    return null;
  }
  if (tile.biome && tile.biome !== 'default') {
    return tile.biome;
  }
  return null;
}

export function subscribeToTerrainTextureUpdates(listener: TerrainTextureUpdateListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
